import { useState, useEffect, useRef } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import {
	Box,
	Button,
	CircularProgress,
	Typography,
	Alert,
	Stack
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { getCurrencySymbol } from '../utils/currencyUtils'


// Load Stripe publishable key from environment based on REACT_APP_ENV
// Note: Create React App requires env vars to start with REACT_APP_
const isDevelopment = process.env.REACT_APP_ENV === 'development';
const stripePublishableKey = isDevelopment
	? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_DEV
	: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Fallback to dev key if prod key is not set
const finalStripeKey = stripePublishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_DEV || '';

const stripePromise = loadStripe(finalStripeKey)

// Card Element styling
const CARD_ELEMENT_OPTIONS = {
	style: {
		base: {
			fontSize: '16px',
			color: '#424770',
			'::placeholder': {
				color: '#aab7c4',
			},
			iconColor: '#666EE8',
		},
		invalid: {
			color: '#9e2146',
			iconColor: '#fa755a',
		},
	},
	hidePostalCode: false,
}

function CheckoutForm({ customerInfo, onSuccess, onError, servicePrice }) {
	const stripe = useStripe()
	const elements = useElements()
	const [processing, setProcessing] = useState(false)
	const [error, setError] = useState(null)
	const [clientSecret, setClientSecret] = useState(null)
	const [loadingSetupIntent, setLoadingSetupIntent] = useState(false)

	// Track if SetupIntent has been created to prevent duplicates
	const setupIntentCreatedRef = useRef(false)
	const lastCustomerEmailRef = useRef(null)
	const currencySymbol = getCurrencySymbol();

	const formattedPrice = servicePrice ? `${currencySymbol}${(servicePrice / 100).toFixed(2)}` : ''

	useEffect(() => {
		// Create SetupIntent when component mounts
		const createSetupIntent = async () => {
			// Prevent duplicate calls (React Strict Mode double-invokes effects)
			if (setupIntentCreatedRef.current && lastCustomerEmailRef.current === customerInfo.email) {
				return
			}

			setupIntentCreatedRef.current = true
			lastCustomerEmailRef.current = customerInfo.email
			setLoadingSetupIntent(true)
			setError(null)

			try {
				// Get Supabase URL and key from environment (match supabase.js logic)
				const isDev = process.env.REACT_APP_ENV === 'development';
				const supabaseUrl = isDev
					? (process.env.REACT_APP_SUPABASE_URL_DEV || process.env.REACT_APP_SUPABASE_URL)
					: process.env.REACT_APP_SUPABASE_URL;
				const supabaseKey = isDev
					? (process.env.REACT_APP_SUPABASE_ANON_KEY_DEV || process.env.REACT_APP_SUPABASE_ANON_KEY)
					: process.env.REACT_APP_SUPABASE_ANON_KEY;

				const response = await fetch(
					`${supabaseUrl}/functions/v1/create-setup-intent`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							'Authorization': `Bearer ${supabaseKey}`
						},
						body: JSON.stringify({
							customerEmail: customerInfo.email,
							customerName: customerInfo.name,
							customerPhone: `+1${customerInfo.phone}`, // Add +1 prefix
							servicePrice: servicePrice
						})
					}
				)

				const data = await response.json()

				if (!response.ok) {
					throw new Error(data.details || data.error || 'Failed to initialize payment')
				}

				setClientSecret(data.clientSecret)

				// Return customerId and setupIntentId for parent component
				if (onSuccess && data.customerId) {
					// Store these for later use
					window.__stripeCustomerId = data.customerId
					window.__stripeSetupIntentId = data.setupIntentId
				}

			} catch (err) {
				console.error('Error creating SetupIntent:', err)
				setError(err.message)
				setupIntentCreatedRef.current = false // Allow retry on error
				if (onError) onError(err)
			} finally {
				setLoadingSetupIntent(false)
			}
		}

		if (customerInfo.email && customerInfo.name && customerInfo.phone) {
			createSetupIntent()
		}
	}, [customerInfo.email, customerInfo.name, customerInfo.phone, onError, onSuccess, servicePrice])

	const handleSubmit = async (event) => {
		event.preventDefault()

		if (!stripe || !elements || !clientSecret) {
			return
		}

		setProcessing(true)
		setError(null)

		try {
			const cardElement = elements.getElement(CardElement)

			// Confirm the SetupIntent with the card details
			const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(
				clientSecret,
				{
					payment_method: {
						card: cardElement,
						billing_details: {
							name: customerInfo.name,
							email: customerInfo.email,
							phone: `+1${customerInfo.phone}`,
						},
					},
				}
			)

			if (stripeError) {
				throw new Error(stripeError.message)
			}

			if (setupIntent.status === 'succeeded') {
				// Payment method saved successfully
				const paymentMethodId = setupIntent.payment_method
				const customerId = window.__stripeCustomerId

				if (onSuccess) {
					onSuccess({
						paymentMethodId,
						customerId,
						setupIntentId: setupIntent.id
					})
				}
			} else {
				throw new Error('Payment method setup failed')
			}

		} catch (err) {
			console.error('Payment error:', err)
			setError(err.message)
			if (onError) onError(err)
		} finally {
			setProcessing(false)
		}
	}

	if (loadingSetupIntent) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
				<CircularProgress size={24} />
				<Typography sx={{ ml: 2 }}>Initializing secure payment...</Typography>
			</Box>
		)
	}

	if (error && !clientSecret) {
		return (
			<Alert severity="error" sx={{ mb: 2 }}>
				{error}
			</Alert>
		)
	}

	return (
		<form onSubmit={handleSubmit}>
			<Stack spacing={3}>

				<Box>
					<Typography variant="subtitle2" gutterBottom>
						Card Information *
					</Typography>
					<Box
						sx={{
							border: '1px solid',
							borderColor: 'divider',
							borderRadius: 1,
							p: 2,
							'&:hover': {
								borderColor: 'primary.main',
							},
							'&:focus-within': {
								borderColor: 'primary.main',
								borderWidth: 2,
							},
						}}
					>
						<CardElement options={CARD_ELEMENT_OPTIONS} />
					</Box>
					<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
						Your card details are securely processed by Stripe. We never store your card number.
					</Typography>
				</Box>

				{error && (
					<Alert severity="error">
						{error}
					</Alert>
				)}

				<Button
					type="submit"
					variant="contained"
					size="large"
					fullWidth
					disabled={!stripe || processing || loadingSetupIntent}
					startIcon={processing ? <CircularProgress size={20} /> : <LockIcon />}
				>
					{processing ? 'Securing Payment Method...' : 'Save Card & Confirm Booking'}
				</Button>

				<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, opacity: 0.6 }}>
					<Typography variant="caption" color="text.secondary">
						Secured by
					</Typography>
					<svg height="20" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
						<path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z" fill="#635BFF" />
					</svg>
				</Box>
			</Stack>
		</form>
	)
}

export default function StripeCheckout({ customerInfo, onSuccess, onError, servicePrice, currencySymbol = '$' }) {
	if (!finalStripeKey) {
		return (
			<Alert severity="error">
				Stripe is not configured. Please add REACT_APP_STRIPE_PUBLISHABLE_KEY_DEV (or REACT_APP_STRIPE_PUBLISHABLE_KEY for prod) to your .env file and restart the server.
			</Alert>
		)
	}

	return (
		<Elements stripe={stripePromise}>
			<CheckoutForm
				customerInfo={customerInfo}
				onSuccess={onSuccess}
				onError={onError}
				servicePrice={servicePrice}
			/>
		</Elements>
	)
}

